/**
 * Audit Log filter bar (storyboard Flow J Frame 1 / Frame 2).
 *
 * Controlled + presentational: the panel owns :type:`AuditFilterState`;
 * this bar renders the controls (time-range, search, actor,
 * outcome, action-kind multi-select, connector-auth toggle) and emits
 * the next state via ``onChange``. The action-kind control is a Radix
 * Popover checkbox list grouped by audit category (mirrors
 * :func:`entity-details/section-visibility-menu`, headers included); the
 * groups come from :data:`AUDIT_ACTION_KIND_GROUPS`, derived from the
 * generated taxonomy, so the control never offers a kind the system does not
 * emit. **Export moved out of
 * this bar** into the panel header's table-settings cog
 * (:func:`audit-column-settings-menu`) — the entry point is no longer here.
 *
 * Cross-references:
 *   - Flow J Frame 1/2 (filter bar + chips); REQ-UI-022.
 *   - REQ-UI-059 (keyboard-navigable controls).
 */
import * as Popover from "@radix-ui/react-popover";
import { CaretDown } from "@phosphor-icons/react";
import {
  AUDIT_ACTION_KIND_GROUPS,
  DEFAULT_AUDIT_FILTER,
  type AuditFilterState,
  OUTCOME_FILTERS,
  OUTCOME_FILTER_LABELS,
  TIME_RANGE_PRESETS,
} from "@/audit/audit-display";
import type { AuditOutcomeFilter } from "@/audit/use-audit-events";

const CONTROL_CLASS =
  "rounded border border-border bg-bg-base px-2 py-1 text-body-sm text-text-primary " +
  "placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent";

export interface AuditFilterBarProps {
  value: AuditFilterState;
  onChange: (next: AuditFilterState) => void;
}

export function AuditFilterBar({ value, onChange }: AuditFilterBarProps): React.ReactElement {
  const patch = (partial: Partial<AuditFilterState>): void => onChange({ ...value, ...partial });

  const toggleKind = (kind: string): void => {
    const next = new Set(value.actionKind);
    if (next.has(kind)) {
      next.delete(kind);
    } else {
      next.add(kind);
    }
    patch({ actionKind: [...next] });
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-border bg-bg-surface px-3 py-2"
      data-testid="audit-filter-bar"
    >
      <select
        aria-label="Time range"
        value={value.timeRangeId}
        onChange={(event) => patch({ timeRangeId: event.target.value })}
        className={CONTROL_CLASS}
        data-testid="audit-filter-time-range"
      >
        {TIME_RANGE_PRESETS.map((preset) => (
          <option key={preset.id} value={preset.id}>
            {preset.label}
          </option>
        ))}
      </select>

      <input
        type="search"
        placeholder="Search actor / action / id"
        aria-label="Search audit log"
        value={value.q}
        onChange={(event) => patch({ q: event.target.value })}
        className={`${CONTROL_CLASS} min-w-[12rem] flex-1`}
        data-testid="audit-filter-search"
      />

      <input
        type="text"
        placeholder="Actor"
        aria-label="Actor"
        value={value.actor}
        onChange={(event) => patch({ actor: event.target.value })}
        className={CONTROL_CLASS}
        data-testid="audit-filter-actor"
      />

      <select
        aria-label="Outcome"
        value={value.outcome}
        onChange={(event) => patch({ outcome: event.target.value as AuditOutcomeFilter })}
        className={CONTROL_CLASS}
        data-testid="audit-filter-outcome"
      >
        {OUTCOME_FILTERS.map((outcome) => (
          <option key={outcome} value={outcome}>
            {OUTCOME_FILTER_LABELS[outcome]}
          </option>
        ))}
      </select>

      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={`${CONTROL_CLASS} flex items-center gap-1`}
            data-testid="audit-filter-action-kind"
          >
            Action kind
            {value.actionKind.length > 0 ? ` (${value.actionKind.length})` : ""}
            <CaretDown size={12} aria-hidden="true" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={6}
            className="z-50 max-h-96 w-96 overflow-y-auto rounded-md border border-border bg-bg-overlay shadow-lg"
            data-testid="audit-filter-action-kind-menu"
            aria-label="Filter by action kind"
          >
            <ul role="menu" className="py-1">
              {AUDIT_ACTION_KIND_GROUPS.map((group) => (
                <li key={group.category} role="none">
                  <h4
                    id={`audit-filter-kind-group-${group.category}`}
                    className="px-3 pb-0.5 pt-2 text-caption font-medium uppercase tracking-wider text-text-secondary"
                    data-testid={`audit-filter-kind-group-${group.category}`}
                  >
                    {group.label}
                  </h4>
                  <ul role="group" aria-labelledby={`audit-filter-kind-group-${group.category}`}>
                    {group.kinds.map((kind) => (
                      <li key={kind} role="none">
                        <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-body-sm text-text-primary hover:bg-bg-elevated">
                          <input
                            type="checkbox"
                            checked={value.actionKind.includes(kind)}
                            onChange={() => toggleKind(kind)}
                            data-testid={`audit-filter-kind-${kind}`}
                            className="h-3.5 w-3.5 cursor-pointer"
                          />
                          <span className="font-mono">{kind}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <label className="flex items-center gap-1.5 text-body-sm text-text-secondary">
        <input
          type="checkbox"
          checked={value.includeConnectorAuth}
          onChange={(event) => patch({ includeConnectorAuth: event.target.checked })}
          data-testid="audit-filter-include-connector-auth"
        />
        Connector auth
      </label>

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(DEFAULT_AUDIT_FILTER)}
          className="rounded px-2 py-1 text-body-sm text-text-secondary hover:bg-bg-elevated"
          data-testid="audit-filter-clear"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
